import React from 'react';
import { ReactRelayContext } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	fireUIEvent as sendUIEvent,
	useForgeUiAnalyticsEvent,
} from '../analytics/useForgeUiAnalyticsEvent';
import { usePerformanceAnalytics } from '../performance-profiling/usePerformanceAnalytics';
import { useInvokeExtension } from '../web-client';
import { useTracingContext } from '../error-reporting';
import type { IframeProps } from '../custom-ui/iframe/types';
import type { BridgeServices, OverlayController } from './types';
import type { ForgeAppError } from './ForgeAppError';
import { EnvironmentContext } from '../context';
import { useProductFetchClient } from '../custom-ui/bridge/methods/useProductFetchClient';
import { usePermissionFetchClient } from '../custom-ui/bridge/methods/permissions/usePermissionFetchClient';
import { useRemoteFetchClient } from '../custom-ui/bridge/methods/useRemoteFetchClient';
import { usePublish } from '@atlaskit/rovo-triggers/main';

const ROVO_TOPIC = 'ai-mate';

type UseBridgeServicesInput = Pick<
	IframeProps,
	'contextIds' | 'extension' | 'getContextToken' | 'coreData'
> & {
	appId: string;
};

/**
 * Assembles host-side capabilities required by bridge methods and returns a
 * stable `getServices` getter that always reflects the latest values.
 *
 * Rather than returning a frozen `BridgeServices` snapshot, the hook updates
 * a `servicesRef` synchronously on every render and exposes a stable getter
 * (created once via `useRef`) that reads from that ref at call time. This
 * ensures all services — `invoker`, `getContextToken`, `productFetchClient`,
 * etc. — are always current when a bridge method is invoked, regardless of
 * which upstream dep changed, without per-service ref boilerplate.
 *
 * Throws at mount time if required capabilities are missing, so bridge methods
 * can use all services without defensive optional-chaining.
 */
export const useBridgeServices = (
	props: UseBridgeServicesInput,
	overlayController: OverlayController,
	renderThreeLOPrompt: (authUrl: string, retry: () => Promise<void>) => Promise<void>,
	showAppError: (error: ForgeAppError) => void,
): { getServices: () => BridgeServices } => {
	// renderThreeLOPrompt is read from props at service-assembly time (see servicesRef below).
	const invoker = useInvokeExtension(props.contextIds, props.extension.id);
	const tracing = useTracingContext();
	const environment = React.useContext(EnvironmentContext);
	const relayContext = React.useContext(ReactRelayContext);

	const isAnonymousAccessSupportedByApp =
		props.extension.properties?.unlicensedAccess?.includes('anonymous') ?? false;

	// Adapts the two-argument renderThreeLOPrompt into the single-argument shape
	// expected by useProductFetchClient. Falls back to a no-op when the extension
	// has no consentUrl (e.g. sandbox/test environments).
	const productRenderThreeLOPrompt = (retry: () => Promise<void>): Promise<void> => {
		const consentUrl = props.extension.consentUrl;
		if (!consentUrl) {
			return Promise.resolve();
		}
		return renderThreeLOPrompt(consentUrl, retry);
	};

	const remoteFetchClient = useRemoteFetchClient();

	const permissionFetchClient = usePermissionFetchClient({
		appId: props.appId,
		installationId: props.extension.installationId,
		contextIds: props.contextIds,
		extensionId: props.extension.id,
		renderThreeLOPrompt: productRenderThreeLOPrompt,
	});

	const productFetchClient = useProductFetchClient({
		appId: props.appId,
		environment,
		contextIds: props.contextIds,
		cloudId: props.coreData.cloudId,
		workspaceId: props.coreData.workspaceId,
		extensionId: props.extension.id,
		isAnonymousAccessSupportedByApp,
		renderThreeLOPrompt: productRenderThreeLOPrompt,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsTracker = useForgeUiAnalyticsEvent();
	const { captureUntrustedEvent } = usePerformanceAnalytics();
	const rovoPublisher = usePublish(ROVO_TOPIC);

	// All hook calls must precede the tracing guard below.
	// servicesRef holds the latest assembled BridgeServices snapshot and is updated
	// synchronously every render. The stable getter reads from it at bridge-call time,
	// so every service (invoker, getContextToken, productFetchClient, etc.) is always
	// current — regardless of which dep changed — without per-service ref boilerplate.
	const servicesRef = React.useRef<BridgeServices | null>(null);
	const getServices = React.useRef<() => BridgeServices>(() => servicesRef.current!).current;

	if (!tracing) {
		throw new Error('WorkerWebRuntime: TracingProvider not found in the component tree. ');
	}

	if (!relayContext?.environment) {
		throw new Error('useBridgeServices: RelayEnvironmentProvider not found in the component tree.');
	}

	// Assembled fresh every render; updates the ref synchronously so any in-flight
	// bridge call that fires after this render always sees the latest values.
	servicesRef.current = {
		invoker,
		getContextToken: props.getContextToken ?? nullGetContextToken,
		tracing,
		overlayController,
		onThreeLO: renderThreeLOPrompt,
		showAppError,
		productFetchClient,
		permissionFetchClient,
		remoteFetchClient,
		relayEnvironment: relayContext.environment,
		analytics: {
			createAnalyticsEvent,
			fireUIEvent: (payload) => sendUIEvent(createAnalyticsEvent, payload),
			tracker: analyticsTracker,
			captureUntrustedEvent,
		},
		rovoPublisher,
	};

	return { getServices };
};

// TODO: currently sandbox does not support getContextToken (unlike other products),
// so we provide a noop implementation to avoid having to guard every bridge method that uses it.
const nullGetContextToken = (() => undefined) as unknown as BridgeServices['getContextToken'];
